<template>
  <div
    class="region-item"
    :class="[regionClass, { 'is-dragging': isDragging }]"
    :style="regionStyle"
    :data-region-id="region.id"
  >
    <!-- 区域内容（只处理点击事件，移动由document级别处理） -->
    <div
      class="region-content"
      @mousedown.stop="onContentMouseDown"
      @mouseup.stop="onContentMouseUp"
    >
      <!-- 签字区 -->
      <template v-if="region.type === 'signature'">
        <span class="placeholder-text signature-text">{{
          region.config.placeholder
        }}</span>
      </template>

      <!-- 文本区 -->
      <template v-else-if="region.type === 'text'">
        <span class="text-content" :style="textStyle">{{
          region.config.content || "点击编辑"
        }}</span>
      </template>

      <!-- 输入区 -->
      <template v-else-if="region.type === 'input'">
        <span class="input-placeholder" :style="textStyle">{{
          region.config.placeholder
        }}</span>
      </template>
    </div>

    <!-- 编辑模式下的控制元素 -->
    <template v-if="editable && selected">
      <!-- 删除按钮 -->
      <div
        class="delete-handle"
        @click.stop="onDelete"
        @mousedown.stop
        @mouseup.stop
      >
        <i class="el-icon-close"></i>
      </div>

      <!-- PC端缩放句柄（居中在边框上，直接处理事件） -->
      <div
        v-if="region.type !== 'text'"
        class="resize-handle-inner resize-handle-top"
        :style="handleInnerTopStyle"
        @mousedown.stop="onHandleMouseDown($event, 'top')"
        @mouseup.stop="onHandleMouseUp($event, 'top')"
      ></div>
      <div
        v-if="region.type !== 'text'"
        class="resize-handle-inner resize-handle-bottom"
        :style="handleInnerBottomStyle"
        @mousedown.stop="onHandleMouseDown($event, 'bottom')"
        @mouseup.stop="onHandleMouseUp($event, 'bottom')"
      ></div>
      <div
        class="resize-handle-inner resize-handle-left"
        :style="handleInnerLeftStyle"
        @mousedown.stop="onHandleMouseDown($event, 'left')"
        @mouseup.stop="onHandleMouseUp($event, 'left')"
      ></div>
      <div
        class="resize-handle-inner resize-handle-right"
        :style="handleInnerRightStyle"
        @mousedown.stop="onHandleMouseDown($event, 'right')"
        @mouseup.stop="onHandleMouseUp($event, 'right')"
      ></div>
    </template>
  </div>
</template>

<script>
export default {
  name: "RegionItem",

  props: {
    region: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    isDragging: {
      type: Boolean,
      default: false,
    },
    containerSize: {
      type: Object,
      default: () => ({ width: 0, height: 0 }),
    },
  },

  data() {
    return {
      cachedScreenWidth: 0,
      cachedPxToRpx: 0,
    };
  },

  created() {
    // PC端使用 window.innerWidth
    this.cachedScreenWidth = window.innerWidth;
    this.cachedPxToRpx = 750 / window.innerWidth;
  },

  computed: {
    regionClass() {
      var classes = ["type-" + this.region.type];
      if (this.selected) {
        classes.push("selected");
      }
      if (this.editable) {
        classes.push("edit-mode");
      }
      return classes.join(" ");
    },

    regionStyle() {
      var position = this.region.position;
      var style =
        "left:" +
        position.x * 100 +
        "%;" +
        "top:" +
        position.y * 100 +
        "%;" +
        "width:" +
        position.width * 100 +
        "%;";

      // 文本区使用固定最小高度，让高度自适应内容
      if (this.region.type === "text") {
        style += "min-height:15px;height:auto;";
      } else {
        style += "height:" + position.height * 100 + "%";
      }
      return style;
    },

    textStyle() {
      var config = this.region.config;
      var fontSizePx = Math.round(config.fontSize);
      return "font-size:" + fontSizePx + "px;color:" + config.fontColor;
    },

    // 句柄尺寸计算
    // 内层：固定正方形（给用户看的，定位在边框上居中）
    // 外层：长方形（真正的触摸区域），动态变化，min 28rpx，max 40rpx
    handleSize() {
      var innerSize = 8; // 内层固定 8px 正方形
      var outerMin = 14; // 外层最小尺寸 14px
      var outerMax = 20; // 外层最大尺寸 20px

      if (!this.containerSize.width || !this.containerSize.height) {
        return {
          inner: innerSize,
          outerMin: outerMin,
          outerMax: outerMax,
          regionWidthRpx: 0,
          regionHeightRpx: 0,
        };
      }

      var position = this.region.position;
      // PC端不需要 rpx 转换
      var pxToRpx = 1;

      // 计算区域的 px 尺寸
      var regionWidthPx = position.width * this.containerSize.width;
      var regionHeightPx = position.height * this.containerSize.height;

      return {
        inner: innerSize,
        outerMin: outerMin,
        outerMax: outerMax,
        regionWidthPx: Math.round(regionWidthPx),
        regionHeightPx: Math.round(regionHeightPx),
      };
    },

    // 上句柄内层样式（居中在边框上，中心点与边框线中心对齐）
    handleInnerTopStyle() {
      var hs = this.handleSize;
      var half = Math.round(hs.inner / 2); // 4px
      return (
        "width:" +
        hs.inner +
        "px;height:" +
        hs.inner +
        "px;position:absolute;top:-" +
        half +
        "px;left:50%;transform:translateX(-50%);"
      );
    },

    // 下句柄内层样式
    handleInnerBottomStyle() {
      var hs = this.handleSize;
      var half = Math.round(hs.inner / 2);
      return (
        "width:" +
        hs.inner +
        "px;height:" +
        hs.inner +
        "px;position:absolute;bottom:-" +
        half +
        "px;left:50%;transform:translateX(-50%);"
      );
    },

    // 左句柄内层样式
    handleInnerLeftStyle() {
      var hs = this.handleSize;
      var half = Math.round(hs.inner / 2);
      return (
        "width:" +
        hs.inner +
        "px;height:" +
        hs.inner +
        "px;position:absolute;left:-" +
        half +
        "px;top:50%;transform:translateY(-50%);"
      );
    },

    // 右句柄内层样式
    handleInnerRightStyle() {
      var hs = this.handleSize;
      var half = Math.round(hs.inner / 2);
      return (
        "width:" +
        hs.inner +
        "px;height:" +
        hs.inner +
        "px;position:absolute;right:-" +
        half +
        "px;top:50%;transform:translateY(-50%);"
      );
    },
  },

  methods: {
    // ========== 内容区域事件 (PC端) ==========
    onContentMouseDown(e) {
      this.$emit("mousedown", e);
    },

    onContentMouseUp(e) {
      this.$emit("mouseup", e);
    },

    // ========== 删除按钮 ==========
    onDelete() {
      this.$emit("delete");
    },

    // ========== 句柄事件 (PC端) ==========
    onHandleMouseDown(e, direction) {
      this.$emit("resize-start", {
        event: { clientX: e.clientX, clientY: e.clientY },
        direction: direction,
      });
    },

    onHandleMouseUp(e, direction) {
      this.$emit("resize-end");
    },
  },
};
</script>

<style lang="scss" scoped>
.region-item {
  position: absolute;
  border-width: 1px;
  box-sizing: border-box;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  // 拖拽时禁用transition，提升性能
  &.is-dragging {
    transition: none !important;
  }

  &.type-signature {
    border-style: solid;
    border-color: #0592ff;
    // backdrop-filter: blur(4rpx);
  }

  &.type-text {
    border-style: solid;
    border-color: #febf02;
    // backdrop-filter: blur(4rpx);
    align-items: flex-start;
    justify-content: flex-start;

    .region-content {
      height: auto;
    }

    .text-content {
      word-wrap: break-word;
      word-break: break-all;
      white-space: pre-wrap;
    }

    .resize-handle-inner {
      border-color: #febf02;
    }

    .resize-handle-inner:active {
      box-shadow: 0 2px 5px rgba(254, 191, 2, 0.35),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
      background: #fffbe6;
    }
  }

  &.type-input {
    border-style: solid;
    border-color: #1bac19;
    // backdrop-filter: blur(4rpx);
    align-items: flex-start;
    justify-content: flex-start;

    .region-content {
      height: auto;
      min-height: 100%;
      overflow: visible;
    }

    .input-placeholder {
      white-space: nowrap;
      line-height: 1.2;
      pointer-events: none;
    }

    .resize-handle-inner {
      border-color: #1bac19;
    }

    .resize-handle-inner:active {
      box-shadow: 0 2px 5px rgba(27, 172, 25, 0.35),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
      background: #e6ffe6;
    }
  }

  &.edit-mode {
    cursor: move;
  }
}

.region-content {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: rgba(230, 230, 230, 0.3);
  box-sizing: border-box;
}

.placeholder-text {
  font-size: 12px;
  color: #0592ff;
  font-weight: 500;
}

.signature-text {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 32px;
}

.text-content {
  word-break: break-all;
  line-height: 1.2;
  display: block;
}

.input-placeholder {
  color: #adb5bd;
  // line-height: 1.2;
  display: block;
}

// 删除按钮（左上角）
.delete-handle {
  position: absolute;
  top: -8px;
  left: -8px;
  width: 16px;
  height: 16px;
  background: #8290a3;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(130, 144, 163, 0.4);
  z-index: 12;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    background: #6b7a8c;
  }

  &:active {
    transform: scale(0.85);
  }

  i {
    font-size: 10px;
    color: #fff;
    line-height: 1;
    font-weight: bold;
  }
}

// PC端缩放句柄（小方块，居中在边框上，可交互）
.resize-handle-inner {
  position: absolute;
  background: #fff;
  border: 1px solid #0592ff;
  border-radius: 2px;
  box-sizing: border-box;
  z-index: 10;
  pointer-events: auto;

  &.resize-handle-top {
    cursor: n-resize;
  }
  &.resize-handle-bottom {
    cursor: s-resize;
  }
  &.resize-handle-left {
    cursor: w-resize;
  }
  &.resize-handle-right {
    cursor: e-resize;
  }
}

// 文本区的缩放句柄颜色
.type-text .resize-handle-inner {
  border-color: #febf02;
}

.type-text .resize-handle-inner:active {
  box-shadow: 0 2px 5px rgba(254, 191, 2, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  background: #fffbe6;
}

// 输入区的缩放句柄颜色
.type-input .resize-handle-inner {
  border-color: #1bac19;
}

.type-input .resize-handle-inner:active {
  box-shadow: 0 2px 5px rgba(27, 172, 25, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  background: #e6ffe6;
}
</style>
